import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Text, Badge, Button, makeStyles, tokens } from "@fluentui/react-components";
import { Open24Regular } from "@fluentui/react-icons";
import { ReviewPointsSnapshot as ReviewPointsSnapshotType } from "../../../services/spreadsheets.service";
import { normalizeStatus, getStatusColor, getStatusColorLight } from "../../../utils/discussionStatusHelpers";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  emptyStatePad: {
    padding: tokens.spacingVerticalXS,
  },
  snapshotTable: {
    width: "100%",
    borderCollapse: "collapse",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: "hidden",
  },
  snapshotThead: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  snapshotTh: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    textAlign: "left",
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  snapshotThActions: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    textAlign: "right" as const,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    width: "32px",
  },
  snapshotTr: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  snapshotTd: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
    verticalAlign: "middle",
  },
  snapshotTdTitle: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
    verticalAlign: "middle",
    maxWidth: "0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  snapshotTdActions: {
    padding: `0 ${tokens.spacingHorizontalXS}`,
    verticalAlign: "middle",
    width: "32px",
    textAlign: "right" as const,
  },
  srOnly: {
    display: "none",
  },
});

interface ReviewPointsSnapshotProps {
  snapshot: ReviewPointsSnapshotType;
  title: string;
}

/**
 * Renders the locked-in snapshot of review points captured at sign-off as a
 * compact table. Each row has an Open button that navigates to Discussions
 * pre-filtered by the review point's title.
 */
const ReviewPointsSnapshot: React.FC<ReviewPointsSnapshotProps> = ({ snapshot, title }) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const navigate = useNavigate();

  const handleGoToReviewPoint = (discussionId: number) => {
    // `from=review` lets the discussion thread's back button return to this
    // Review page instead of the discussion list.
    navigate(`/discussions?discussionId=${discussionId}&from=review`);
  };

  return (
    <div className={styles.section}>
      <Text className={typography.subsectionTitle}>
        {title} ({snapshot.reviewPoints.length})
      </Text>
      <Text className={typography.caption}>
        Snapshot from {new Date(snapshot.snapshotTime).toLocaleString()}
      </Text>
      {snapshot.reviewPoints.length === 0 ? (
        <Text className={`${typography.emptyStateBody} ${styles.emptyStatePad}`}>
          No open review points at time of signing.
        </Text>
      ) : (
        <table className={styles.snapshotTable}>
          <thead className={styles.snapshotThead}>
            <tr>
              <th className={styles.snapshotTh}>Review Point</th>
              <th className={styles.snapshotTh}>Status</th>
              <th className={styles.snapshotThActions}><span className={styles.srOnly}>Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {snapshot.reviewPoints.map((rp) => (
              <tr key={rp.discussionId} className={styles.snapshotTr}>
                <td className={styles.snapshotTdTitle}>{rp.title}</td>
                <td className={styles.snapshotTd}>
                  <Badge
                    size="small"
                    appearance="filled"
                    style={{
                      backgroundColor: getStatusColorLight(rp.status),
                      color: getStatusColor(rp.status),
                    }}
                  >
                    {normalizeStatus(rp.status)}
                  </Badge>
                </td>
                <td className={styles.snapshotTdActions}>
                  <Button
                    size="small"
                    icon={<Open24Regular />}
                    appearance="subtle"
                    onClick={() => handleGoToReviewPoint(rp.discussionId)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ReviewPointsSnapshot;
